import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  ActiveChallenge,
  ChallengeTemplate,
  ChallengeHistoryItem,
  ChallengeStats,
  StreakData,
  StartChallengeRequest,
  UpdateChallengeProgressRequest
} from '../../types/challenge';
import { ChallengeAPI } from '../../lib/ChallengeSystem';

// Async thunks
export const fetchActiveChallenges = createAsyncThunk(
  'challenges/fetchActive',
  async (coupleId: string) => {
    const response = await ChallengeAPI.getActiveChallenges(coupleId);
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch active challenges');
    }
    return response.challenges || [];
  }
);

export const fetchAvailableChallenges = createAsyncThunk(
  'challenges/fetchAvailable',
  async (coupleId: string) => {
    const response = await ChallengeAPI.getAvailableChallenges(coupleId);
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch available challenges');
    }
    return response.templates || [];
  }
);

export const startNewChallenge = createAsyncThunk(
  'challenges/start',
  async (request: StartChallengeRequest) => {
    const response = await ChallengeAPI.startChallenge(request);
    if (!response.success) {
      throw new Error(response.error || 'Failed to start challenge');
    }
    return response.challenge!;
  }
);

export const updateChallengeProgress = createAsyncThunk(
  'challenges/updateProgress',
  async (request: UpdateChallengeProgressRequest) => {
    const response = await ChallengeAPI.updateChallengeProgress(request);
    if (!response.success) {
      throw new Error(response.error || 'Failed to update challenge progress');
    }
    return {
      challenge: response.challenge!,
      completedChallenges: response.completed_challenges || [],
      pointsAwarded: response.points_awarded || 0,
      newAchievements: response.new_achievements || []
    };
  }
);

export const fetchChallengeHistory = createAsyncThunk(
  'challenges/fetchHistory',
  async (coupleId: string) => {
    const response = await ChallengeAPI.getChallengeHistory(coupleId);
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch challenge history');
    }
    return {
      history: response.history || [],
      stats: response.stats || null
    };
  }
);

export const updateMultipleChallenges = createAsyncThunk(
  'challenges/updateMultiple',
  async (params: { coupleId: string; challengeIds: string[]; expenseId?: string }) => {
    const { coupleId, challengeIds, expenseId } = params;
    const responses = await ChallengeAPI.updateMultipleChallengeProgress(
      coupleId, 
      challengeIds, 
      expenseId
    );
    
    const updatedChallenges: ActiveChallenge[] = [];
    const completedChallenges: ChallengeHistoryItem[] = [];
    let totalPointsAwarded = 0;
    const allNewAchievements: string[] = [];

    responses.forEach(response => {
      if (response.success && response.challenge) {
        updatedChallenges.push(response.challenge);
        if (response.completed_challenges) {
          completedChallenges.push(...response.completed_challenges);
        }
        if (response.points_awarded) {
          totalPointsAwarded += response.points_awarded;
        }
        if (response.new_achievements) {
          allNewAchievements.push(...response.new_achievements);
        }
      }
    });

    return {
      updatedChallenges,
      completedChallenges,
      totalPointsAwarded,
      newAchievements: allNewAchievements
    };
  }
);

interface ChallengeState {
  // Active challenges
  activeChallenges: ActiveChallenge[];
  
  // Available challenge templates
  availableTemplates: ChallengeTemplate[];
  
  // Challenge history
  challengeHistory: ChallengeHistoryItem[];
  
  // Statistics
  stats: ChallengeStats | null;
  
  // Streak data
  streakData: StreakData | null;
  
  // Loading states
  loading: {
    activeChallenges: boolean;
    availableTemplates: boolean;
    challengeHistory: boolean;
    startingChallenge: boolean;
    updatingProgress: boolean;
  };
  
  // Error states
  errors: {
    activeChallenges: string | null;
    availableTemplates: string | null;
    challengeHistory: string | null;
    startingChallenge: string | null;
    updatingProgress: string | null;
  };
  
  // UI state
  selectedTemplate: ChallengeTemplate | null;
  showStartChallengeDialog: boolean;
  
  // Notifications
  recentCompletions: ChallengeHistoryItem[];
  pointsAwarded: number;
  newAchievements: string[];
}

const initialState: ChallengeState = {
  activeChallenges: [],
  availableTemplates: [],
  challengeHistory: [],
  stats: null,
  streakData: null,
  
  loading: {
    activeChallenges: false,
    availableTemplates: false,
    challengeHistory: false,
    startingChallenge: false,
    updatingProgress: false,
  },
  
  errors: {
    activeChallenges: null,
    availableTemplates: null,
    challengeHistory: null,
    startingChallenge: null,
    updatingProgress: null,
  },
  
  selectedTemplate: null,
  showStartChallengeDialog: false,
  recentCompletions: [],
  pointsAwarded: 0,
  newAchievements: [],
};

const challengeSlice = createSlice({
  name: 'challenges',
  initialState,
  reducers: {
    // Clear errors
    clearErrors: (state) => {
      state.errors = {
        activeChallenges: null,
        availableTemplates: null,
        challengeHistory: null,
        startingChallenge: null,
        updatingProgress: null,
      };
    },
    
    // Clear specific error
    clearError: (state, action: PayloadAction<keyof ChallengeState['errors']>) => {
      state.errors[action.payload] = null;
    },
    
    // UI actions
    selectTemplate: (state, action: PayloadAction<ChallengeTemplate | null>) => {
      state.selectedTemplate = action.payload;
    },
    
    setShowStartChallengeDialog: (state, action: PayloadAction<boolean>) => {
      state.showStartChallengeDialog = action.payload;
    },
    
    // Clear notifications
    clearNotifications: (state) => {
      state.recentCompletions = [];
      state.pointsAwarded = 0;
      state.newAchievements = [];
    },
    
    // Update streak data
    updateStreakData: (state, action: PayloadAction<StreakData>) => {
      state.streakData = action.payload;
    },
    
    // Manually update challenge progress (for optimistic updates)
    updateChallengeProgressOptimistic: (state, action: PayloadAction<{
      challengeId: string;
      progress: number;
      progressPercentage: number;
    }>) => {
      const challenge = state.activeChallenges.find(c => c.id === action.payload.challengeId);
      if (challenge) {
        challenge.current_progress = action.payload.progress;
        challenge.progress_percentage = action.payload.progressPercentage;
      }
    },
    
    // Reset challenge state
    resetChallengeState: (state) => {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    // Fetch active challenges
    builder
      .addCase(fetchActiveChallenges.pending, (state) => {
        state.loading.activeChallenges = true;
        state.errors.activeChallenges = null;
      })
      .addCase(fetchActiveChallenges.fulfilled, (state, action) => {
        state.loading.activeChallenges = false;
        state.activeChallenges = action.payload;
      })
      .addCase(fetchActiveChallenges.rejected, (state, action) => {
        state.loading.activeChallenges = false;
        state.errors.activeChallenges = action.error.message || 'Failed to fetch active challenges';
      });
    
    // Fetch available challenges
    builder
      .addCase(fetchAvailableChallenges.pending, (state) => {
        state.loading.availableTemplates = true;
        state.errors.availableTemplates = null;
      })
      .addCase(fetchAvailableChallenges.fulfilled, (state, action) => {
        state.loading.availableTemplates = false;
        state.availableTemplates = action.payload;
      })
      .addCase(fetchAvailableChallenges.rejected, (state, action) => {
        state.loading.availableTemplates = false;
        state.errors.availableTemplates = action.error.message || 'Failed to fetch available challenges';
      });
    
    // Start new challenge
    builder
      .addCase(startNewChallenge.pending, (state) => {
        state.loading.startingChallenge = true;
        state.errors.startingChallenge = null;
      })
      .addCase(startNewChallenge.fulfilled, (state, action) => {
        state.loading.startingChallenge = false;
        state.activeChallenges.unshift(action.payload);
        state.selectedTemplate = null;
        state.showStartChallengeDialog = false;
      })
      .addCase(startNewChallenge.rejected, (state, action) => {
        state.loading.startingChallenge = false;
        state.errors.startingChallenge = action.error.message || 'Failed to start challenge';
      });
    
    // Update challenge progress
    builder
      .addCase(updateChallengeProgress.pending, (state) => {
        state.loading.updatingProgress = true;
        state.errors.updatingProgress = null;
      })
      .addCase(updateChallengeProgress.fulfilled, (state, action) => {
        state.loading.updatingProgress = false;
        
        const { challenge, completedChallenges, pointsAwarded, newAchievements } = action.payload;
        
        // Update the challenge in the active list
        const index = state.activeChallenges.findIndex(c => c.id === challenge.id);
        if (index !== -1) {
          state.activeChallenges[index] = challenge;
        }
        
        // Handle completed challenges
        if (completedChallenges.length > 0) {
          state.recentCompletions = completedChallenges;
          state.challengeHistory.unshift(...completedChallenges);
          
          // Remove completed challenges from active list
          state.activeChallenges = state.activeChallenges.filter(
            c => !completedChallenges.some(completed => completed.id === c.id)
          );
        }
        
        // Set notifications
        if (pointsAwarded > 0) {
          state.pointsAwarded = pointsAwarded;
        }
        if (newAchievements.length > 0) {
          state.newAchievements = newAchievements;
        }
        
        // Note: Achievement checking is handled in the backend updateChallengeProgress function
      })
      .addCase(updateChallengeProgress.rejected, (state, action) => {
        state.loading.updatingProgress = false;
        state.errors.updatingProgress = action.error.message || 'Failed to update challenge progress';
      });
    
    // Fetch challenge history
    builder
      .addCase(fetchChallengeHistory.pending, (state) => {
        state.loading.challengeHistory = true;
        state.errors.challengeHistory = null;
      })
      .addCase(fetchChallengeHistory.fulfilled, (state, action) => {
        state.loading.challengeHistory = false;
        state.challengeHistory = action.payload.history;
        state.stats = action.payload.stats;
      })
      .addCase(fetchChallengeHistory.rejected, (state, action) => {
        state.loading.challengeHistory = false;
        state.errors.challengeHistory = action.error.message || 'Failed to fetch challenge history';
      });
    
    // Update multiple challenges
    builder
      .addCase(updateMultipleChallenges.pending, (state) => {
        state.loading.updatingProgress = true;
        state.errors.updatingProgress = null;
      })
      .addCase(updateMultipleChallenges.fulfilled, (state, action) => {
        state.loading.updatingProgress = false;
        
        const { updatedChallenges, completedChallenges, totalPointsAwarded, newAchievements } = action.payload;
        
        // Update active challenges
        updatedChallenges.forEach(updatedChallenge => {
          const index = state.activeChallenges.findIndex(c => c.id === updatedChallenge.id);
          if (index !== -1) {
            state.activeChallenges[index] = updatedChallenge;
          }
        });
        
        // Handle completed challenges
        if (completedChallenges.length > 0) {
          state.recentCompletions = completedChallenges;
          state.challengeHistory.unshift(...completedChallenges);
          
          // Remove completed challenges from active list
          state.activeChallenges = state.activeChallenges.filter(
            c => !completedChallenges.some(completed => completed.id === c.id)
          );
        }
        
        // Set notifications
        if (totalPointsAwarded > 0) {
          state.pointsAwarded = totalPointsAwarded;
        }
        if (newAchievements.length > 0) {
          state.newAchievements = newAchievements;
        }
      })
      .addCase(updateMultipleChallenges.rejected, (state, action) => {
        state.loading.updatingProgress = false;
        state.errors.updatingProgress = action.error.message || 'Failed to update challenges';
      });
  },
});

export const {
  clearErrors,
  clearError,
  selectTemplate,
  setShowStartChallengeDialog,
  clearNotifications,
  updateStreakData,
  updateChallengeProgressOptimistic,
  resetChallengeState,
} = challengeSlice.actions;

export default challengeSlice.reducer;